import React from "react";
import { Button, Form } from "react-bootstrap";
import { XLg, PlusLg } from "react-bootstrap-icons";

const recalcMinSqft = (rules) => {
  return rules.map((rule, idx) => ({
    ...rule,
    min_sqft: idx === 0 ? 0 : (rules[idx - 1].max_sqft || 0) + 1,
  }));
};

/**
 * Sort bounded rows by max_sqft ascending, keeping duration paired.
 * The last (unbounded, max_sqft: null) row always stays last.
 */
const sortAndRecalc = (rules) => {
  const lastRow = rules[rules.length - 1];
  const bounded = rules.slice(0, -1);

  bounded.sort((a, b) => (a.max_sqft || 0) - (b.max_sqft || 0));

  return recalcMinSqft([...bounded, lastRow]);
};

const ServiceConfigEditor = ({ durationRules, onChange, errors }) => {
  const overlapping = errors?.overlapping || new Set();

  const handleMaxSqftChange = (idx, value) => {
    const updated = [...durationRules];
    updated[idx] = {
      ...updated[idx],
      max_sqft: value === "" ? "" : Number(value),
    };
    // Don't sort during typing — just recalc min_sqft
    onChange(recalcMinSqft(updated));
  };

  const handleMaxSqftBlur = (idx, value) => {
    if (value === "" || value == null) return;
    const num = Number(value);
    if (isNaN(num)) return;
    // Round to nearest 100, minimum 100
    const rounded = Math.max(Math.round(num / 100) * 100, 100);
    const updated = [...durationRules];
    updated[idx] = { ...updated[idx], max_sqft: rounded };
    // Sort rows by max_sqft on blur, duration travels with its sqft
    onChange(sortAndRecalc(updated));
  };

  const handleDurationChange = (idx, value) => {
    const updated = [...durationRules];
    const val = value === "" ? "" : Math.max(1, Math.floor(Number(value)));
    updated[idx] = { ...updated[idx], duration_minutes: val };
    onChange(updated);
  };

  const handleDeleteRow = (idx) => {
    if (durationRules.length <= 1) return;
    // Cannot delete the last (unbounded) row
    if (idx === durationRules.length - 1) return;
    const updated = [...durationRules];
    updated.splice(idx, 1);
    onChange(recalcMinSqft(updated));
  };

  const handleAddRow = () => {
    const updated = [...durationRules];
    const lastIdx = updated.length - 1;

    if (updated.length === 1) {
      // Only one unbounded row (0+) — split into two
      // New row: 0→1000, duration = copy existing
      const newRow = {
        min_sqft: 0,
        max_sqft: 1000,
        duration_minutes: updated[0].duration_minutes,
      };
      updated.splice(0, 0, newRow);
    } else {
      // Insert above last row
      // Sqft = last bounded row's max + 1000
      // Duration = last bounded row's duration + 30
      const lastBounded = updated[lastIdx - 1];
      const prevMax = lastBounded.max_sqft || 0;
      const newMaxSqft = Math.round((prevMax + 1000) / 100) * 100;
      const newDuration = (lastBounded.duration_minutes || 0) + 30;

      const newRow = {
        min_sqft: prevMax + 1,
        max_sqft: newMaxSqft,
        duration_minutes: newDuration,
      };
      updated.splice(lastIdx, 0, newRow);
    }

    onChange(recalcMinSqft(updated));
  };

  return (
    <div className="booking-settings-section">
      <div className="cbs-svc-header">
        <h4 className="booking-settings-section-title mb-0">
          Service Configuration
        </h4>
        <Button
          variant="outline-primary"
          size="sm"
          className="cbs-svc-add-btn"
          onClick={handleAddRow}
        >
          <PlusLg size={12} className="me-1" />
          Size Range
        </Button>
      </div>
      <table className="service-config-table cbs-svc-table">
        <thead>
          <tr>
            <th className="cbs-svc-delete-col"></th>
            <th className="cbs-svc-size-header">Property Size (sq ft)</th>
            <th className="cbs-svc-duration-header">Base Duration (min)</th>
          </tr>
        </thead>
        <tbody>
          {durationRules.map((rule, idx) => {
            const isLast = idx === durationRules.length - 1;
            const hasOverlap = overlapping.has(idx);

            return (
              <tr
                key={idx}
                className={hasOverlap ? "cbs-svc-overlap-row" : ""}
              >
                <td className="cbs-svc-delete-col">
                  {!isLast && durationRules.length > 1 && (
                    <Button
                      variant="link"
                      size="sm"
                      className="cbs-svc-delete-btn"
                      onClick={() => handleDeleteRow(idx)}
                      aria-label={`Delete size range row ${idx + 1}`}
                    >
                      <XLg size={14} />
                    </Button>
                  )}
                </td>
                <td className="cbs-svc-size-cell">
                  {isLast ? (
                    <span className="cbs-svc-unbounded-label">
                      {(rule.min_sqft || 0).toLocaleString()}+
                    </span>
                  ) : (
                    <div className="cbs-svc-range-row">
                      <span className="cbs-svc-min-sqft">
                        {(rule.min_sqft || 0).toLocaleString()}
                      </span>
                      <span className="cbs-svc-range-arrow">&rarr;</span>
                      <Form.Control
                        type="number"
                        min={100}
                        max={9999}
                        value={rule.max_sqft ?? ""}
                        onChange={(e) =>
                          handleMaxSqftChange(idx, e.target.value)
                        }
                        onBlur={(e) =>
                          handleMaxSqftBlur(idx, e.target.value)
                        }
                        className="cbs-svc-sqft-input"
                      />
                    </div>
                  )}
                </td>
                <td className="cbs-svc-duration-cell">
                  <Form.Control
                    type="number"
                    min={1}
                    value={rule.duration_minutes ?? ""}
                    onChange={(e) =>
                      handleDurationChange(idx, e.target.value)
                    }
                    className="cbs-svc-duration-input"
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default ServiceConfigEditor;
